import { NotificationSchedule as NotificationScheduleEnum } from '@gripp/shared-logic';
import { Control, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { RadioButton } from 'react-native-paper';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { Colors } from '../../themes/colors';
import { Button } from '../crop-circle/Button';
import { preferencesStyles } from './styles';

type NotificationScheduleProps = {
  control: Control<any>;
  disabled?: boolean;
};

export const NotificationSchedule = ({
  control,
  disabled,
}: NotificationScheduleProps) => {
  const scheduleDaysName = 'notificationPreferences.schedule.scheduleDays';
  const startTimeName = 'notificationPreferences.schedule.startTime';
  const endTimeName = 'notificationPreferences.schedule.endTime';

  const { t } = useTranslation();

  const scheduleDaysOptions = useMemo(
    () => [
      {
        label: t('preferences.notifications.schedule.weekDays') || 'Week Days',
        value: NotificationScheduleEnum.WeekDays,
      },
      {
        label: t('preferences.notifications.schedule.everyDay') || 'Every Day',
        value: NotificationScheduleEnum.EveryDay,
      },
    ],
    [t]
  );

  const startTime = useWatch({
    control: control,
    name: startTimeName,
  });

  const endTime = useWatch({
    control: control,
    name: endTimeName,
  });

  const getTimeString = (date: Date): string => {
    return date
      ? new Date(date).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('preferences.notifications.schedule.notSet');
  };

  return (
    <View style={preferencesStyles.contentContainer}>
      <View>
        <Text style={preferencesStyles.heading}>
          SCHEDULE
        </Text>
        <View style={preferencesStyles.contentFieldForm}>
          {/* Radio Buttons for Weekday/Every Day */}
          <View style={styles.radioContainer}>
            <Controller
              control={control}
              name={scheduleDaysName}
              render={({ field: { onChange, value } }) => (
                <View style={styles.radioButtonsContainer}>
                  {scheduleDaysOptions.map((option) => (
                    <Pressable
                      key={option.value}
                      style={styles.radioButtonRow}
                      onPress={() => onChange(option.value)}
                      disabled={disabled}
                    >
                      <Text style={styles.radioLabel}>
                        {option.label}
                      </Text>
                      <RadioButton.Android
                        value={option.value}
                        status={option.value === value ? 'checked' : 'unchecked'}
                        onPress={() => onChange(option.value)}
                      />
                    </Pressable>
                  ))}
                </View>
              )}
            />
          </View>

          {/* Start Time */}
          <TimeInputRow
            control={control}
            name={startTimeName}
            label={t('preferences.notifications.schedule.startTime')}
            defaultHours={9}
            defaultMinutes={0}
            disabled={disabled}
            getTimeString={getTimeString}
            timeValue={startTime}
          />

          {/* End Time */}
          <TimeInputRow
            control={control}
            name={endTimeName}
            label={t('preferences.notifications.schedule.endTime')}
            defaultHours={17}
            defaultMinutes={0}
            disabled={disabled}
            getTimeString={getTimeString}
            timeValue={endTime}
          />
        </View>
      </View>
    </View>
  );
};

type TimeInputRowProps = {
  control: Control<any>;
  name: string;
  label: string;
  defaultHours: number;
  defaultMinutes: number;
  disabled?: boolean;
  getTimeString: (date: Date) => string;
  timeValue: Date | undefined;
};

const TimeInputRow = ({
  control,
  name,
  label,
  defaultHours,
  defaultMinutes,
  disabled,
  getTimeString,
  timeValue,
}: TimeInputRowProps) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const timeString = getTimeString(value || timeValue);
        const hasTime = value || timeValue;

        return (
          <>
            <View style={styles.timeRow}>
              <View style={styles.timeLabelContainer}>
                <Text style={styles.timeLabel}>{label}</Text>
                {hasTime && (
                  <Text style={styles.timeValue}>{timeString}</Text>
                )}
              </View>
              <Button
                variant="rounded"
                state="active"
                onPress={() => setVisible(true)}
                disabled={disabled}
                style={styles.setButton}
              >
                {t('preferences.notifications.schedule.set')}
              </Button>
            </View>
            <DateTimePickerModal
              textColor={Colors.grayText}
              cancelTextIOS={t('preferences.notifications.schedule.cancel')}
              confirmTextIOS={t('preferences.notifications.schedule.accept')}
              isVisible={visible}
              mode="time"
              display="spinner"
              date={
                value
                  ? new Date(value)
                  : new Date(1970, 0, 1, defaultHours, defaultMinutes)
              }
              onConfirm={(date) => {
                setVisible(false);
                onChange(date);
              }}
              onCancel={() => setVisible(false)}
            />
          </>
        );
      }}
    />
  );
};

const styles = StyleSheet.create({
  radioContainer: {
    // No padding - padding is on radioButtonRow to match timeRow
  },
  radioButtonsContainer: {
    flexDirection: 'column',
  },
  radioButtonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    width: '100%',
  },
  radioLabel: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '400',
    lineHeight: 19,
    flex: 1,
  },
  timeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  timeLabelContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  timeLabel: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '400',
    lineHeight: 19,
  },
  timeValue: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '400',
    lineHeight: 19,
  },
  setButton: {
    minWidth: 80,
  },
});
